import {isTelegramWebAppAvailable} from "./telegramWebApp";

const ANALYTICS_TOKEN = process.env.REACT_APP_TELEGRAM_ANALYTICS_TOKEN || "";
const ANALYTICS_APP_NAME = process.env.REACT_APP_TELEGRAM_ANALYTICS_APP_NAME || "";
const CLIENT_LOG_ENDPOINT = process.env.REACT_APP_CLIENT_LOG_ENDPOINT || "";
const ANALYTICS_MAX_RETRIES = 7;
const ANALYTICS_RETRY_DELAYS_MS = [700, 1500, 3000, 5000, 8000, 13000, 21000];
const ERROR_LOG_THROTTLE_MS = 60 * 1000;
const ANALYTICS_APP_NAME_MARKER_KEY = "tg_analytics_app_name";

let isAnalyticsInitialized = false;
let isAnalyticsReady = false;
let analyticsRetryCount = 0;
let analyticsRetryTimeoutId = null;
let lastErrorLogAt = 0;

// Возвращаем true только после успешного init SDK.
export const isTelegramAnalyticsReady = () => isAnalyticsReady;

// Возвращаем SDK из CDN-скрипта, если он уже загружен.
const getTelegramAnalyticsSdk = () => {
    if (typeof window === "undefined") {
        return null;
    }

    const sdk = window.telegramAnalytics;
    if (!sdk || typeof sdk.init !== "function") {
        return null;
    }

    return sdk;
};

// Чистим очередь SDK, если имя приложения изменилось и в storage остались старые батчи.
const cleanupStaleAnalyticsQueue = () => {
    if (typeof window === "undefined" || !window.localStorage) {
        return;
    }

    try {
        const previousAppName = window.localStorage.getItem(ANALYTICS_APP_NAME_MARKER_KEY) || "";
        if (previousAppName === ANALYTICS_APP_NAME) {
            return;
        }

        const storages = [window.localStorage, window.sessionStorage].filter(Boolean);
        storages.forEach((storage) => {
            Object.keys(storage)
                .filter((key) => key.startsWith("TGA-Batch-Requests"))
                .forEach((key) => storage.removeItem(key));
        });

        window.localStorage.setItem(ANALYTICS_APP_NAME_MARKER_KEY, ANALYTICS_APP_NAME);
    } catch (error) {
        if (process.env.NODE_ENV === "development") {
            console.warn("[analytics] queue cleanup failed", error);
        }
    }
};

// Отправляем ошибку аналитики на backend не чаще, чем раз в минуту.
const reportAnalyticsError = (error, stage) => {
    const now = Date.now();
    if (now - lastErrorLogAt < ERROR_LOG_THROTTLE_MS) {
        return;
    }

    lastErrorLogAt = now;

    if (!CLIENT_LOG_ENDPOINT) {
        return;
    }

    const payload = {
        source: "telegram-analytics",
        stage,
        message: error?.message || "unknown_error",
        stack: error?.stack || "",
        retryCount: analyticsRetryCount,
        path: typeof window !== "undefined" ? window.location.pathname : "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        createdAt: new Date().toISOString(),
    };
    const body = JSON.stringify(payload);

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([body], {type: "application/json"});
        navigator.sendBeacon(CLIENT_LOG_ENDPOINT, blob);
        return;
    }

    fetch(CLIENT_LOG_ENDPOINT, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body,
        keepalive: true,
    }).catch(() => {});
};

// Планируем повторную инициализацию, если Telegram WebApp/SDK стали доступны позже.
const scheduleAnalyticsInitRetry = () => {
    if (analyticsRetryTimeoutId || analyticsRetryCount >= ANALYTICS_MAX_RETRIES) {
        return;
    }

    const retryDelay = ANALYTICS_RETRY_DELAYS_MS[Math.min(analyticsRetryCount, ANALYTICS_RETRY_DELAYS_MS.length - 1)];
    analyticsRetryTimeoutId = setTimeout(() => {
        analyticsRetryTimeoutId = null;
        analyticsRetryCount += 1;
        initTelegramAnalytics();
    }, retryDelay);
};

// Подключаем аналитику один раз при старте приложения.
export const initTelegramAnalytics = () => {
    if (isAnalyticsInitialized) {
        return;
    }

    if (!ANALYTICS_TOKEN || !ANALYTICS_APP_NAME) {
        return;
    }

    const analyticsSdk = getTelegramAnalyticsSdk();
    if (!analyticsSdk) {
        scheduleAnalyticsInitRetry();
        return;
    }

    if (!isTelegramWebAppAvailable()) {
        scheduleAnalyticsInitRetry();
        return;
    }

    cleanupStaleAnalyticsQueue();

    isAnalyticsInitialized = true;
    isAnalyticsReady = false;

    analyticsSdk.init({
        token: ANALYTICS_TOKEN,
        appName: ANALYTICS_APP_NAME,
    }).then(() => {
        isAnalyticsReady = true;
        analyticsRetryCount = 0;

        if (analyticsRetryTimeoutId) {
            clearTimeout(analyticsRetryTimeoutId);
            analyticsRetryTimeoutId = null;
        }
    }).catch((error) => {
        isAnalyticsInitialized = false;
        isAnalyticsReady = false;
        scheduleAnalyticsInitRetry();
        reportAnalyticsError(error, "init");

        if (process.env.NODE_ENV === "development") {
            console.error("[analytics] init failed", error);
        }
    });
};
